/**
 * PostComposer Tray Composable
 *
 * Extracted from PostComposer.vue — handles the InlineMediaTray
 * integration: mixed-media items list, selection, removal, reorder.
 *
 * Zero behavior change: this is a structural extraction only.
 */

import { computed } from "vue";

/**
 * @param {Object} deps - External dependencies from PostComposer
 * @param {import('vue').Ref} deps.videoFile - Current video File ref
 * @param {import('vue').Ref} deps.existingVideoUrl - Existing video URL ref
 * @param {import('vue').Ref} deps.images - Images array ref
 * @param {import('vue').Ref} deps.documents - Documents array ref
 * @param {import('vue').Ref} deps.thumbnailIndex - Active photo index ref
 * @param {import('vue').Ref} deps.activeDocIndex - Active document index ref
 * @param {import('vue').Ref} deps.selectedType - Current media type ref
 * @param {import('vue').ComputedRef} deps.videoPreviewUrl - Video preview URL computed
 * @param {Function} deps.thumbnailSrc - Returns display URL for an image
 * @param {Function} deps.mediaPreviewSrc - Returns the actual media preview URL
 * @param {Function} deps.mediaTypeForItem - Returns image/video for a media item
 * @param {import('vue').ComputedRef} deps.standaloneVideoPosterSrc - Poster URL for standalone video
 * @param {Function} deps.removeImageAt - Removes image at index
 * @param {Function} deps.onEditThumb - Sets thumbnail index
 * @param {Function} deps.onTrayReorder - Reorders images array
 * @param {Function} deps.updateSelectedTypeFromContent - Re-derives selectedType
 */
export function usePostComposerTray(deps) {
  const {
    videoFile,
    existingVideoUrl,
    images,
    documents,
    thumbnailIndex,
    activeDocIndex,
    selectedType,
    videoPreviewUrl,
    thumbnailSrc,
    mediaPreviewSrc,
    mediaTypeForItem,
    standaloneVideoPosterSrc,
    removeImageAt,
    onEditThumb,
    onTrayReorder,
    updateSelectedTypeFromContent,
  } = deps;

  const hasVideoInTray = computed(
    () => !!videoFile.value || !!existingVideoUrl.value,
  );

  const mediaItemsForTray = computed(() => {
    const items = [];
    if (hasVideoInTray.value) {
      items.push({
        src: videoPreviewUrl.value,
        poster: standaloneVideoPosterSrc?.value || "",
        type: "video",
        key: "vid_0",
      });
    }
    (images.value || []).forEach((img, i) => {
      const mediaType =
        typeof mediaTypeForItem === "function" ? mediaTypeForItem(img) : "image";
      items.push({
        src:
          mediaType === "video" && typeof mediaPreviewSrc === "function"
            ? mediaPreviewSrc(img)
            : thumbnailSrc(img),
        poster: mediaType === "video" ? thumbnailSrc(img) : "",
        type: mediaType,
        key: `img_${i}`,
      });
    });
    (documents.value || []).forEach((d, i) =>
      items.push({
        src: d.url || "",
        type: "file",
        name: d.name,
        key: `doc_${d.id || i}`,
      }),
    );
    return items;
  });

  const selectedIndexForTray = computed(() => {
    const offset = hasVideoInTray.value ? 1 : 0;
    const imgLen = images.value?.length || 0;
    if (selectedType.value === "photos" && imgLen) {
      return offset + Math.max(0, Math.min(thumbnailIndex.value, imgLen - 1));
    }
    const docLen = documents.value?.length || 0;
    if (selectedType.value === "documents" && docLen) {
      return (
        offset +
        imgLen +
        Math.max(0, Math.min(activeDocIndex.value, docLen - 1))
      );
    }
    return (
      offset +
      Math.max(0, Math.min(thumbnailIndex.value, Math.max(0, imgLen - 1)))
    );
  });

  const onTraySelect = (i) => {
    const offset = hasVideoInTray.value ? 1 : 0;
    if (hasVideoInTray.value && i === 0) {
      selectedType.value = "video";
      return;
    }
    const imgLen = images.value?.length || 0;
    if (i >= offset && i < offset + imgLen) {
      thumbnailIndex.value = i - offset;
      selectedType.value = hasVideoInTray.value ? "video" : "photos";
      return;
    }
    const docStart = offset + imgLen;
    const docLen = documents.value?.length || 0;
    if (i >= docStart && i < docStart + docLen) {
      activeDocIndex.value = i - docStart;
      selectedType.value = "documents";
      return;
    }
  };

  const removeMediaAt = (i) => {
    let idx = i;
    if (hasVideoInTray.value) {
      if (idx === 0) {
        if (videoFile.value) {
          videoFile.value = null;
        } else {
          existingVideoUrl.value = "";
        }
        updateSelectedTypeFromContent();
        return;
      }
      idx -= 1;
    }
    const imgLen = images.value?.length || 0;
    if (idx < imgLen) {
      removeImageAt(idx);
      return;
    }
    const docIdx = idx - imgLen;
    if (docIdx >= 0 && docIdx < (documents.value?.length || 0)) {
      const arr = documents.value.slice();
      const removedDoc = arr.splice(docIdx, 1)[0];
      try {
        if (
          removedDoc?.url &&
          typeof removedDoc.url === "string" &&
          removedDoc.url.startsWith("blob:")
        ) {
          URL.revokeObjectURL(removedDoc.url);
        }
      } catch {}
      documents.value = arr;
    }
    updateSelectedTypeFromContent();
  };

  const onTrayEdit = (i) => {
    const offset = hasVideoInTray.value ? 1 : 0;
    if (i >= offset && i < offset + (images.value?.length || 0))
      onEditThumb(i - offset);
  };

  const onTrayReorderMixed = ({ from, to }) => {
    const offset = hasVideoInTray.value ? 1 : 0;
    const imgStart = offset;
    const imgEnd = offset + (images.value?.length || 0) - 1;
    if (from < imgStart || from > imgEnd || to < imgStart || to > imgEnd)
      return;
    onTrayReorder(from - offset, to - offset);
  };

  return {
    hasVideoInTray,
    mediaItemsForTray,
    selectedIndexForTray,
    onTraySelect,
    removeMediaAt,
    onTrayEdit,
    onTrayReorderMixed,
  };
}
